/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import io from "socket.io-client";
import store from "../store/store";
import {
    participantMovement,
    addNewJoiner,
    updateRoomData,
    resetInitialRoomState,
    updateActiveRooms,
    removePendingRequest,
    removeInvitation,
    setMyJoiningRequest,
} from "../store/slice/roomSlice";
import { updateHomeRoom } from "../update/updateHomeRoom";
import { addActiveUser, removeActiveUser, setEngines, updateEngine, updateUser } from "../store/slice/userSlice";
import { updateJobRoom } from "../update/updatejobRoom";
import { updateExploreRoom } from "../update/updateExploreRoom";
import { updateTopologyRoom } from "../update/updateTopologyRoom";
import { setNotification, setReason, setTours } from "../store/slice/privateSlice";
import { resetInitialGraphState, setRotation } from "../store/slice/centerGraphSlice";
import { getCurrentRoomData } from "../update/getCurrentRoomData";
import { updateCurrentRoom } from "../update/updateCurrentRoom";
import { setCanModify, setCooperative, setTourName } from "../store/slice/privateSlice";
import { updateWorkstationRoom } from "../update/updateWorkstationRoom";
import { updateDelayAnalysisRoom } from "../update/updateDelayAnalysisRoom";
import { setClaraResponse, setClaraEnable, setClaraEnv, setClaraWaiting } from "../store/slice/claraSlice";
import { handleLogout, handleParticipantLeftRoom } from "../handlers/RoomHandlers";
import { resetInitialHomeRoom } from "../store/slice/roomSlices/homeSlice";
import { setChecked } from "../store/slice/userSlice";
import { handleRequestAcceptedAs, newInvitation, newJoiningRequest } from "../handlers/TroubleshootingPanelHandlers";
import { updateMute } from "../store/slice/roomSlice"
import { CONTRIBUTOR, INVITATION, JOINING_REQUEST, OWNER, VIEWER } from "../store/constants/sharedModeFields";
import * as webRTCHandler from "../handlers/webRTCHandler";

let socket = null

export const connectWithSocketServer = () => {
    const inCooperative = window.location.pathname.includes("cooperative");

    socket = io(`wss://${process.env.REACT_APP_BACKEND_HOST || window.location.hostname
        }:${process.env.REACT_APP_BACKEND_PORT || window.location.port}`);

    // new socket listeners 
    socket.on("connected", () => {
        console.log("successfully connected with socket.io server");

        if (!inCooperative) getUserEngines();
        else store.dispatch(setCooperative(true));

        getClaraInfo()
    });

    // joining request
    socket.on("request-declined", () => {
        store.dispatch(setNotification("Your last join request was declined"))
        store.dispatch(setMyJoiningRequest(false))
    })

    socket.on("request-accepted", (data) => {
        const { joinerSocketId, role, roomId } = data
        handleRequestAcceptedAs(joinerSocketId, role, roomId)
    })

    socket.on("invitation-declined", (data) => {
        const { username, socketId } = data
        store.dispatch(removeInvitation({ socketId }))
        store.dispatch(setNotification(username + " has declined your invitation"))
    })

    socket.on("invitation-accepted", (data) => {
        const { socketId } = data
        store.dispatch(removeInvitation({ socketId }))
        store.dispatch(removePendingRequest({ socketId, type: INVITATION }))
    })

    socket.on("connected-user", (data) => {
        store.dispatch(addActiveUser(data))
    })

    socket.on("disconnected-user", (data) => {
        store.dispatch(removeActiveUser(data))
    })

    // User Listener
    socket.on("engines-list", (engines) => {
        store.dispatch(setEngines(engines));
    });

    socket.on("test-connection-result", (engine) => {
        store.dispatch(updateEngine(engine));
    });

    socket.on("send-tvrexcertificates", (data) => {
        store.dispatch(setTours(data))
        store.dispatch(setTourName(
            data.includes("FIRST USER EXPERIENCE") ?
                "FIRST USER EXPERIENCE" : false)
        )
    })

    // Wa Data Listener
    socket.on("send-homeroomdata", (data) => {
        const { reduxPath, res } = data;
        updateHomeRoom(reduxPath, res);
    });

    socket.on("send-jobroomdata", (data) => {
        const { reduxPath, res } = data;
        updateJobRoom(reduxPath, res);
    });

    socket.on("send-exploreroomdata", (data) => {
        const { reduxPath, res } = data;
        updateExploreRoom(reduxPath, res);
    });

    socket.on("send-topologyroomdata", (data) => {
        const { reduxPath, res } = data;
        updateTopologyRoom(reduxPath, res);
    });

    socket.on("send-delayanalysisroomdata", (data) => {
        const { reduxPath, res } = data;
        updateDelayAnalysisRoom(reduxPath, res);
    });

    socket.on("send-workstationroomdata", (data) => {
        const { reduxPath, res } = data;
        updateWorkstationRoom(reduxPath, res)
    });

    socket.on("send-currentroomdata", (data) => {
        updateCurrentRoom(data);
    });

    socket.on("get-currentroomdata", (data) => {
        const newJoinerSocketId = data;
        getCurrentRoomData(socket, newJoinerSocketId);
    });

    socket.on("job-history-error", () => {
        store.dispatch(setNotification("You have to Enable Reporting on this DWC."))
    })

    // Room Listener
    socket.on("active-rooms", (data) => {
        const { activeRooms } = data
        store.dispatch(updateActiveRooms(activeRooms))
    })

    socket.on("new-joiningrequest", (data) => {
        const { joinerUsername, joinerSocketId } = data
        newJoiningRequest(joinerSocketId, joinerUsername)
    })

    socket.on("new-invitation", (data) => {
        const { inviterSocketId, inviterUsername, roomId, role } = data
        newInvitation(inviterSocketId, inviterUsername, roomId, role)
    })

    socket.on("new-joiner", (data) => {
        store.dispatch(addNewJoiner(data))
    })

    socket.on("update-room", (data) => {
        const { participants } = data
        const currentUserSocketId = store.getState().user.data.socketId
        const updateCurrentUser = participants.find((participant) => {
            return participant.socketId === currentUserSocketId
        })

        store.dispatch(updateRoomData(data))
        store.dispatch(updateUser(updateCurrentUser))
        store.dispatch(setCanModify(updateCurrentUser.role === CONTRIBUTOR))
    });

    socket.on("delete-room", () => {
        const pendingRequests = store.getState().room.pendingRequests
        for (const pr in pendingRequests)
            declineRequest({ socketId: pendingRequests[pr].socketId })

        leaveRoom({
            roomId: store.getState().room.roomId,
            participantSocketId: socket.id
        })

        store.dispatch(resetInitialRoomState())
        store.dispatch(setCooperative(false))
    });

    // Clara Listener
    socket.on("clara-response", (response) => {
        store.dispatch(setClaraResponse(response))
        store.dispatch(setClaraWaiting(false))
    });

    socket.on("clara-env", (response) => {
        const version = response[0]?.attachment?.current_env;

        store.dispatch(setClaraEnv(version));
    });

    socket.on("send-clara-info", (isClaraEnabled) => {
        store.dispatch(setClaraEnable(isClaraEnabled))
    })

    socket.on("set-participants", (data) => {
        const { leavingParticipantSocketId } = data
        handleParticipantLeftRoom(leavingParticipantSocketId)
    })

    socket.on("conn-prepare", (data) => {
        const { connUserSocketId } = data;
        webRTCHandler.prepareNewPeerConnection(connUserSocketId, false);
        socket.emit("conn-init", { connUserSocketId: connUserSocketId });
    });

    socket.on("conn-init", (data) => {
        const { connUserSocketId } = data;
        webRTCHandler.prepareNewPeerConnection(connUserSocketId, true);
    });

    socket.on("conn-signal", (data) => {
        webRTCHandler.handleSignalingData(data);
    });

    socket.on("toggle-mute", (data) => {
        store.dispatch(updateMute(data))
    })

    socket.on("mute-user", () => {
        const localStream = store.getState().room.localStream;
        localStream.getAudioTracks()[0].enabled = !localStream.getAudioTracks()[0].enabled
    })

    socket.on("update-player", (data) => {
        store.dispatch(participantMovement(data));
    });

    socket.on("update-center-graph", (data) => {
        const callback = data
        store.dispatch(setRotation(callback))
    });

    socket.on("send-action", (data) => {
        const payload = data.payload
        if (!(payload instanceof Function)) store.dispatch(data)
    });

    // abort listener
    socket.on("force-abort-server-raises", () => {
        if (!store.getState().private.abort.reason) {
            store.dispatch(setNotification("Something went wrong, have you the permissions on this engine ?"));
            store.dispatch(resetInitialHomeRoom());
            store.dispatch(resetInitialGraphState());
            store.dispatch(setChecked([]));
        }

        store.dispatch(setReason(false))
    })

    // disconnected 
    socket.on("disconnected", () => {
        handleLogout()
    });

    return socket;
};

// User request
export const login = (data, checkCallback) => {
    if (socket) socket.emit("login", data, checkCallback);
};

export const sendJoiningRequest = (data) => {
    if (socket) socket.emit("send-joiningrequest", data);
};

export const sendInvitation = (data) => {
    if (socket) socket.emit("send-invitation", data);
};

export const acceptInvitation = (data) => {
    const { inviterSocketId, roomId, role } = data

    const socketId = store.getState().user.data.socketId

    store.dispatch(removePendingRequest({ socketId: inviterSocketId, type: INVITATION }))
    handleRequestAcceptedAs(socketId, role, roomId)

    if (socket) socket.emit("accept-invitation", { socketId: inviterSocketId });
}

export const acceptContributor = (data) => {
    const { joinerSocketId } = data
    const currentRoomId = store.getState().room.roomId
    store.dispatch(removePendingRequest({ socketId: joinerSocketId, type: JOINING_REQUEST }))

    if (socket) socket.emit("accept-request", { ...data, role: CONTRIBUTOR, roomId: currentRoomId });
}

export const acceptViewer = (data) => {
    const { joinerSocketId } = data
    const currentRoomId = store.getState().room.roomId
    store.dispatch(removePendingRequest({ socketId: joinerSocketId, type: JOINING_REQUEST }))

    if (socket) socket.emit("accept-request", { ...data, role: VIEWER, roomId: currentRoomId });
}

export const declineRequest = (data) => {
    const { joinerSocketId } = data
    store.dispatch(removePendingRequest({ socketId: joinerSocketId, type: JOINING_REQUEST }))

    if (socket) socket.emit("decline-request", data);
}

export const declineInvitation = (data) => {
    const { inviterSocketId } = data
    const username = store.getState().user.data.username
    const socketId = store.getState().user.data.socketId

    store.dispatch(removePendingRequest({ socketId: inviterSocketId, type: INVITATION }))

    if (socket) socket.emit("decline-invitation", { inviterSocketId, username, socketId })
}

export const abort = (reason) => {
    if (socket) {
        store.dispatch(setReason(reason))
        socket.emit("abort");
    }
};

export const getUserEngines = () => {
    if (socket) socket.emit("get-engines");
}

export const getTvrexCertificates = () => {
    if (socket) socket.emit("get-tvrexcertificates")
}

export const setTvrexCertificate = (data) => {
    if (socket) socket.emit("set-tvrexcertificate", data)
}

// request WA data through socket 
export const getHomeRoomData = (data) => {
    if (socket) socket.emit("get-homeroomdata", data);
}

export const getJobRoomData = (data) => {
    if (socket) socket.emit("get-jobroomdata", data);
}

export const getExploreRoomData = (data) => {
    if (socket) socket.emit("get-exploreroomdata", data)
}

export const getTopologyRoomData = (data) => {
    if (socket) socket.emit("get-topologyroomdata", data)
}

export const getWorkstationRoomData = (data) => {
    if (socket) socket.emit("get-workstationroomdata", data)
}

export const getDelayAnalysisRoomData = (data) => {
    if (socket) socket.emit("get-delayanalysisroomdata", data)
}

// multiuser room
export const createRoom = (data, callback) => {
    if (socket) socket.emit("room-create", data, callback);
};

export const joinRoom = (data, callback) => {
    if (socket) socket.emit("room-join", data, callback);
};

export const leaveRoom = (data) => {
    if (socket) socket.emit("room-leave", data)
}

export const deleteRoom = (data) => {
    if (socket) socket.emit("room-delete", data)
}

export const signalPeerData = (data) => {
    socket.emit("conn-signal", data);
};

export const sendMicState = (micState) => {
    if (socket) {
        socket.emit("send-mic-state", { micState });
        store.dispatch(updateMute({ socketId: socket.id, muted: micState }))
    }
}

// multiuser player
export const updatePlayer = (position, rotation) => {
    const roomId = store.getState().room?.roomId;
    if (socket) socket.emit('update-player', {
        rotation,
        position,
        socketId: store.getState().user.data.socketId,
        roomId: roomId ? roomId : OWNER,
    });
};

export const makeAll = (role, roomId) => {
    if (socket) socket.emit("make-all", { role, roomId })
}

// multiuser generic sharing action
export const sendAction = (action, roomId) => {
    const participants = store.getState().room.participants
    if (socket && participants.length > 1)
        socket.emit("send-action", action, roomId);
};

// multiuser centerGraph
export const sendUpdateCenterGraph = (callback) => {
    const roomId = store.getState().room?.roomId;

    if (socket) socket.emit("update-center-graph", roomId, callback)
}

// clara
export const getClaraInfo = () => {
    if (socket) socket.emit("get-clara-info")
}

export const sendClaraIntent = (message) => {
    // if the use asking for show more button o show more... (inform & page)
    // he doesn't need to see the waiting status of clara
    if (["page", "inform"].every(e => !message.includes(e)))
        store.dispatch(setClaraWaiting(true))

    if (socket) socket.emit("chat-with-clara", message);
};

// export const requestDataUpdates = (currentRoom, data) => {
//     let currentLoadingState = store.getState().shared.loadingState[currentRoom];
//     //this will disable all requests until the server has stopped loading
//     if (currentLoadingState) {
//         if (currentLoadingState.isLoading) {
//             return;
//         }
//     }
//     //this will disable all requests in a context if an autorefresh is already active
//     if (window.timeoutID) {
//         if (store.getState().shared.history[0].ID === currentRoom) {
//             return;
//         } else {
//             clearTimeout(window.timeoutID);
//             window.timeoutID = null;
//         }
//     }
//     if (socket) socket.emit("requestDataUpdates", currentRoom, data);
// };